import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { fetchHandshake } from "@/services/handshake";
import {
  getAmastyMostviewedRelatedProductsBySku,
  getProductBySku,
  getProductFaqs,
  getProductsBySkus,
} from "@/services/product";

import Album from "@/components/product/Album";
import Details from "@/components/product/Details";
const FreeGifts = dynamic(() => import("@/components/product/FreeGifts"));
const Tabs = dynamic(() => import("@/components/product/tabs"));
const ProductsBulk = dynamic(() => import("@/components/product/ProductsBulk"));
const PDPBottom = dynamic(() => import("@/components/product/PDPBottom"));
import {
  getMultiProductsRating,
  getProductReviews,
} from "@/services/product/reviews";
import useModifyProductReview from "@/hooks/product-reviews/useModifyProductReview";
import { useEffect, useRef, useState, useMemo } from "react";
const BuildBundleModal = dynamic(() => import("@/components/modals/Bundle"));
import { productSkuString, skusArrayHandler } from "@/helpers/product";
import { event_view_item } from "@/utils/gtm";
import useSWR from "swr";
import { retainKeys } from "@/utils/product";
import ProductSeo from "@/components/product/ProductSeo";
import dynamic from "next/dynamic";
import { getCookie } from "cookies-next";
import useOnClickOutside from "@/hooks/useOnClickOutside";

const PDPAWS = dynamic(() => import("@/components/product/PDPAWS"));
const PDP24Stream = dynamic(() => import("@/components/product/PDP24Stream"));

const FlixMedia = dynamic(() => import("@/components/product/Flixmedia"));

const ProductPage = ({ product: rawProduct, locale }) => {
  const [fetchProductReviews, setFetchProductReviews] = useState(false);
  const [fetchGetAmastySkus, setFetchGetAmastySkus] = useState(false);
  const [fetchRelatedProducts, setFetchRelatedProducts] = useState(false);
  const [fetchUpSellProducts, setFetchUpSellProducts] = useState(false);
  const [parentQty, setParentQty] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [fetchRelatedAndUpSellRatings, setFetchRelatedAndUpSellRatings] =
    useState(false);
  const [fetchProductFaqs, setFetchProductFaqs] = useState(false);
  useEffect(() => {
    if (rawProduct) {
      event_view_item(rawProduct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productSku = productSkuString(rawProduct?.sku);

  const { t } = useTranslation("product");

  // total product reviews
  const { data: productReviews } = useSWR(
    fetchProductReviews ? `productReviews/${productSku}/${locale}` : null,
    () => getProductReviews(productSku, locale),
    {
      revalidateOnFocus: false,
    }
  );

  // get related and up sell products skus from amasty if no related or up sell products
  const isGetAmastySkus =
    rawProduct?.custom_related_products?.length === 0 ||
    rawProduct?.up_sell_products?.length === 0;

  const { data: productFaqsRes } = useSWR(
    fetchProductFaqs ? `getProductFaqs/${productSku}/${locale}` : null,
    () => getProductFaqs(productSku, getCookie("handshake")),
    {
      revalidateOnFocus: false,
      revalidateOnMount: fetchProductFaqs,
    }
  );

  // console.log('productFaqsRes//////////////////', productFaqsRes?.data?.data)

  const { data: amastyProductsRes } = useSWR(
    fetchGetAmastySkus ? `getAmastySkus/${productSku}/${locale}` : null,
    () =>
      getAmastyMostviewedRelatedProductsBySku(
        getCookie("handshake"),
        productSku,
        10
      ),
    {
      revalidateOnFocus: false,
      revalidateOnMount: isGetAmastySkus,
    }
  );

  // get related and up sell products data
  const relatedSkus = skusArrayHandler(
    rawProduct?.custom_related_products?.length > 0
      ? rawProduct?.custom_related_products
      : amastyProductsRes?.data?.data?.[0]?.related?.length > 0
      ? amastyProductsRes?.data?.data?.[0]?.related
      : []
  );
  const upSellSkus = skusArrayHandler(
    rawProduct?.up_sell_products?.length > 0
      ? rawProduct?.up_sell_products
      : amastyProductsRes?.data?.data?.[0]?.upsell?.length > 0
      ? amastyProductsRes?.data?.data?.[0]?.upsell
      : []
  );

  const { data: relatedProductsRes } = useSWR(
    fetchRelatedProducts ? `relatedProducts/${relatedSkus}/${locale}` : null,
    () => getProductsBySkus(getCookie("handshake"), relatedSkus, false),
    {
      revalidateOnFocus: false,
      revalidateOnMount: relatedSkus?.length > 0,
    }
  );
  const { data: upSellProductsRes } = useSWR(
    fetchUpSellProducts ? `upSellProducts/${upSellSkus}/${locale}` : null,
    () => getProductsBySkus(getCookie("handshake"), upSellSkus, false),
    {
      revalidateOnFocus: false,
      revalidateOnMount: upSellSkus?.length > 0,
    }
  );

  const relatedProducts = useMemo(
    () =>
      relatedProductsRes?.data?.data?.products?.filter(
        (item) => item?.stock?.is_in_stock
      ) || [],
    [relatedProductsRes]
  );
  const upSellProducts = useMemo(
    () =>
      upSellProductsRes?.data?.data?.products?.filter(
        (item) => item?.stock?.is_in_stock
      ) || [],
    [upSellProductsRes]
  );

  // get related and up sell products ratings
  const relatedAndUpSellSkus = skusArrayHandler([
    ...relatedSkus,
    ...upSellSkus,
  ]);

  const { data: relatedAndUpSellRatingsRes } = useSWR(
    fetchRelatedAndUpSellRatings
      ? `relatedAndUpSellRatings/${relatedAndUpSellSkus}/${locale}`
      : null,
    () => getMultiProductsRating(relatedAndUpSellSkus, getCookie("handshake")),
    {
      revalidateOnFocus: false,
      revalidateOnMount: relatedAndUpSellSkus?.length > 0,
    }
  );

  const relatedAndUpSellRatingsData = useMemo(
    () => relatedAndUpSellRatingsRes?.data?.data?.at(1) || [],
    [relatedAndUpSellRatingsRes]
  );

  const [product, setProduct] = useState(rawProduct);
  const [open, setOpen] = useState(false);
  const {
    productScore,
    totalReviewsNum,
    // totalScoreForEachRate,
    // totalPercentageForEachRate,
  } = useModifyProductReview(productReviews?.data);
  const ref = useRef();
  useOnClickOutside([ref], () => {
    setOpen(false);
    setSelectedProducts([]);
  });

  useEffect(() => {
    setProduct(rawProduct);
  }, [rawProduct]);

  useEffect(() => {
    setTimeout(() => {
      setFetchProductReviews(true);
      setFetchGetAmastySkus(true);
      setFetchRelatedProducts(true);
      setFetchUpSellProducts(true);
      setFetchRelatedAndUpSellRatings(true);
      setFetchProductFaqs(true);
    }, 5000);
  }, []);

  const bundleSKUs = [
    {
      sku: typeof product?.sku == "string" ? product?.sku : product?.sku?.at(0),
      qty: parentQty,
      ...(product?.type_id == "configurable" && {
        product_type: "configurable",
        product_option: {
          extension_attributes: {
            configurable_item_options: Object.values(
              retainKeys(product?.selectedConfigurations, [
                "attribute_id",
                "option_id",
              ])
            ),
          },
        },
      }),
    },
    ...selectedProducts?.map((p) => ({
      sku: typeof p.sku == "string" ? p?.sku : p?.sku?.at(0),
      qty: p?.qty,
    })),
  ];
  return (
    <section className="overflow-x-hidden">
      <div className="videoPlayerPDP">
        <ProductSeo product={product} locale={locale} />
        <div className="my-4 sm:my-8 sm:*:mb-8">
          <div className="grid w-11/12 grid-cols-12 gap-4 mx-auto">
            <Album product={product} />
            <Details
              product={product}
              setOpen={setOpen}
              productScore={productScore}
              relatedProducts={relatedProducts}
              totalReviewsNum={totalReviewsNum}
              setProduct={setProduct}
            />
          </div>
          {product?.product_fees?.length > 0 ? (
            <FreeGifts productFees={product?.product_fees} />
          ) : null}
          <section className="w-11/12 mx-auto">
            {relatedProducts?.length ? (
              <ProductsBulk
                title={t("Related Products")}
                products={relatedProducts.map((product) => ({
                  ...product,
                  rating: Object.entries(relatedAndUpSellRatingsData)
                    ?.find((r) => r?.at(0) === product.sku)
                    ?.at(1) || { count: "0", rating: "0.0" },
                }))}
                locale={locale}
              />
            ) : null}
          </section>
          <section className="w-11/12 mx-auto">
            {upSellProducts?.length ? (
              <ProductsBulk
                title={t("Recommended Accessories")}
                products={upSellProducts.map((product) => ({
                  ...product,
                  rating: Object.entries(relatedAndUpSellRatingsData)
                    ?.find((r) => r?.at(0) === product.sku)
                    ?.at(1) || { count: "0", rating: "0.0" },
                }))}
                locale={locale}
              />
            ) : null}
          </section>
          <PDPAWS
            productSku={productSku}
            mainCategory={product?._category_?.main_category?.category_id}
          />

          <div className="sm:w-11/12 sm:mx-auto">
            <Tabs
              {...{
                parentProduct: product || {},
                bundleSKUs: bundleSKUs || [],
                description: product?.description || [],
                specifications: product?.specifications_home_appliances || [],
                product_attributes: product?.product_attributes || [],
                accessories: product?.accessories || [],
                productReviews: productReviews?.data || {},
                bundle_discounts: product?.bundle_discounts || [],
                add_on_savings: product?.add_on_savings || [],
                relatedProducts: relatedProducts || [],
                faqs: productFaqsRes?.data?.data || [],
              }}
            />
          </div>

          <div id="flix-inpage" />
          <div id="flix-minisites" />

          <FlixMedia product={product} />
        </div>
        <PDPBottom />
        {open && (
          <section className="fixed top-0 left-0 flex items-center justify-center w-screen h-screen bg-black bg-opacity-60 z-modal">
            <div
              ref={ref}
              className="w-11/12 p-4 max-h-[90vh] overflow-y-auto mx-auto bg-white rounded-md"
            >
              <BuildBundleModal
                {...{
                  product,
                  setProduct,
                  setOpen,
                  relatedProducts,
                  bundleSKUs,
                  parentQty,
                  setParentQty,
                  selectedProducts,
                  setSelectedProducts,
                  bundles: product?.ulmod_bundles,
                  t,
                  ref,
                }}
              />
            </div>
          </section>
        )}
      </div>
      <PDP24Stream
        brand={product?.option_text_brand?.[0]}
        sku={Array.isArray(product?.sku) ? product?.sku?.[0] : product?.sku}
        locale={locale}
      />
    </section>
  );
};

export default ProductPage;

export async function getServerSideProps({
  locale,
  req,
  res,
  params: { rewrite_url },
}) {
  const parsedSku = rewrite_url?.slice(rewrite_url.lastIndexOf("-p-") + 3); // parsed sku from url

  // get handshake from cookies or generate new handshake
  const handshake = await fetchHandshake({ req, res, locale });

  // fetch product data
  const { data: product, error } = await getProductBySku(handshake, parsedSku);
  console.log("product===========================================", product);
  if (
    product?.data === null ||
    error ||
    (Array.isArray(product?.data) && product?.data?.length === 0)
  ) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      product: product?.data?.at(0) || [],
      ...(await serverSideTranslations(locale, ["common", "product"])),
    },
  };
}
